import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, AlertCircle, FileText, HelpCircle } from 'lucide-react';
import Button from '../components/ui/Button';

const Insurance: React.FC = () => {
  const coverageFeatures = [
    {
      title: 'Utilsigtet skade',
      icon: <Shield className="h-6 w-6" />,
      description: 'Dækning af utilsigtede skader på lånt værktøj op til 2.500 kr.',
      included: true,
    },
    {
      title: 'Tyverisikring',
      icon: <Shield className="h-6 w-6" />,
      description: 'Beskyttelse mod tyveri når værktøjet opbevares sikkert.',
      included: true,
    },
  ];

  const notCovered = [
    'Forsætlig skade eller misbrug af værktøj',
    'Skader opstået ved ukorrekt brug',
    'Tab eller tyveri ved uagtsom opbevaring',
    'Skader ikke rapporteret inden for 48 timer',
  ];

  const claimSteps = [
    {
      step: '1',
      title: 'Rapporter skade',
      description: 'Kontakt både ejeren og vores support inden for 48 timer efter hændelsen.',
    },
    {
      step: '2',
      title: 'Dokumentation',
      description: 'Tag fotos af skaden og beskriv hvad der skete så detaljeret som muligt.',
    },
    {
      step: '3',
      title: 'Vurdering',
      description: 'Vores team vil vurdere sagen og guide dig gennem processen.',
    },
    {
      step: '4',
      title: 'Afklaring',
      description: 'Vi hjælper med at afklare skaden og eventuel kompensation.',
    },
  ];

  const faqs = [
    {
      question: 'Hvornår træder forsikringen i kraft?',
      answer: 'Forsikringen er aktiv fra det øjeblik du modtager værktøjet og indtil det returneres til ejeren.',
    },
    {
      question: 'Skal jeg betale en selvrisiko?',
      answer: 'Ved utilsigtede skader under normal brug er der ingen selvrisiko. Ved grov uagtsomhed kan der opkræves en selvrisiko.',
    },
    {
      question: 'Hvad hvis værktøjet bliver stjålet?',
      answer: 'Tyveri er dækket hvis værktøjet blev opbevaret forsvarligt og tyveriet rapporteres til politiet inden for 24 timer.',
    },
    {
      question: 'Hvor lang tid tager skadesbehandling?',
      answer: 'De fleste sager behandles inden for 5-7 hverdage. Komplekse sager kan tage op til 14 dage.',
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-green-600 text-white">
        <div className="container mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl mb-6">
              Forsikringsdækning
            </h1>
            <p className="text-xl text-green-100">
              Alle lån gennem DoMore inkluderer grundlæggende forsikring. 
              Få ro i sindet med beskyttelse mod utilsigtede skader.
            </p>
          </div>
        </div>
      </div>

      {/* Coverage Overview */}
      <div className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Hvad er dækket?</h2>
            <p className="mt-4 text-xl text-gray-600">
              Automatisk beskyttelse på alle udlån
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {coverageFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-100 text-green-600 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Not Covered Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center mb-6">
              <AlertCircle className="h-8 w-8 text-red-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Hvad er ikke dækket?</h2>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <ul className="space-y-3">
                {notCovered.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-600 mr-3">•</span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Claims Process */}
      <div className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <FileText className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900">Sådan anmelder du en skade</h2>
            <p className="mt-4 text-xl text-gray-600">
              En enkel proces til at håndtere utilsigtede hændelser
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {claimSteps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="flex items-center justify-center h-12 w-12 rounded-full bg-green-600 text-white text-xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <a href="mailto:support@do-more.site?subject=Forsikringskrav">
              <Button variant="primary">
                Anmeld skade
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center mb-12">
              <HelpCircle className="h-8 w-8 text-green-600 mr-3" />
              <h2 className="text-3xl font-bold text-gray-900">Ofte stillede spørgsmål</h2>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-lg p-6 border border-gray-200"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Har du flere spørgsmål?</h2>
            <p className="text-gray-300 mb-8">
              Vores supportteam er klar til at hjælpe dig med alle forsikringsrelaterede spørgsmål.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="primary">
                  Kontakt support
                </Button>
              </Link>
              <Link to="/help">
                <Button variant="secondary">
                  Besøg hjælpecenter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Insurance;
